<template>
  <v-dialog v-model="opened">
    <v-card class="basic-modal" elevation="4" width="560">
      <v-card-title class="d-flex justify-space-between">
        <h6 class="heading--text d-flex align-center">
          <v-icon size="24" class="mr-4" color="primary">$lock</v-icon>
          <span> {{ $t("reseller.apiTokens.modal.create.title") }}</span>
        </h6>
        <v-btn v-if="!submitting" icon x-small @click="close()" :style="!confirm ? 'opacity: 0;' : ''">
          <v-icon size="24">$close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div>
          <v-form ref="form" class="d-flex flex-column">
            <div v-if="createdToken" class="d-flex flex-column">
              <label class="mb-2">
                {{ $t("reseller.apiTokens.name") }}
              </label>
              <v-text-field
                outlined
                v-model="name"
                class="v-input--md"
                hide-details="auto"
                dense
                readonly
                disabled
                style="pointer-events: none;
                background-color: var(--v-gray-lighten6);
                color: var(--v-gray-base);
                opacity: 0.5;"
              ></v-text-field>
              <label class="mb-2">
                {{ $t("reseller.apiTokens.content") }}
              </label>
              <div
                class="well well--gray py-3 px-4"
                >
                <ul class="record-box-list">
                  <li
                    class="record-box-list-item"
                    @click="copyElement($event)"
                    @mouseover="hoveredIndex.spf = `1-name`"
                    @mouseleave="hoveredIndex.spf = null, valueCopied = false"
                  >
                    <span class="record-box-copy-element heading--text">
                      <span>{{ createdToken }}</span>
                      <v-tooltip
                        open-delay="150"
                        bottom
                        content-class="copy-record-val-tooltip"
                        transition="custom-tooltip"
                        :value="hoveredIndex.spf === `1-name`"
                      >
                        <template v-slot:activator="{ attrs }">
                          <v-icon
                            class="copy-icon"
                            :size="16"
                            v-bind="attrs"
                          >
                            $copy
                          </v-icon>
                        </template>
                        <div>
                          <p class="p-3 mb-0 record-box-copy-element-tooltip">
                            <span v-if="valueCopied" class="copied primary--text">{{ $t('tooltip.copied') }}</span>
                            <span v-else class="copy">{{ $t('tooltip.copy') }}</span>
                          </p>
                        </div>
                      </v-tooltip>
                    </span>
                  </li>
                </ul>
              </div>
              <div class="alert alert--error-bg alert--sm alert--100 mt-2">
                <div class="alert__icon-box">
                  <v-icon color="error" size="20">$alertcircle</v-icon>
                </div>
                <div class="alert__text-box alert__text-box--wide d-flex flex-row" v-html="$t('reseller.apiTokens.modal.created')">
                </div>
              </div>
              <div class="d-flex flex-column mb-6 mt-5">
                <div class="modal-divider mb-5"></div>
                <p class="p-2">
                  <b>{{ $t("message.confirmBeforeProceedingAction") }}</b>
                </p>
                <Checkbox
                  outlined
                  :checked="confirm"
                  @change="confirm = $event"
                  :label="$t('reseller.apiTokens.savedToken')"
                  color="primary"
                  dense
                />
              </div>
            </div>
            <div v-else class="d-flex flex-column mb-6">
              <label class="mb-2">
                {{ $t("reseller.apiTokens.name") }}
              </label>

              <div :class="'last-child'">
                <v-text-field
                  outlined
                  :placeholder="$t('reseller.apiTokens.name')"
                  v-model="name"
                  class="v-input--md"
                  hide-details="auto"
                  validate-on-blur
                  dense
                ></v-text-field>
              </div>
            </div>
          </v-form>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end align-end pb-8 pt-0">
        <v-btn
          v-if="!createdToken"
          :text="true"
          large
          elevation="0"
          class="normal"
          @click="!submitting && close()"
        >
          <span class="p-2 font-weight-bold gray--text text--darken-1">
            {{ $t("button.cancel") }}
          </span>
        </v-btn>
        <v-btn
          v-if="!createdToken"
          :text="false"
          large
          elevation="0"
          class="primary"
          @click="submit()"
          :loading="submitting"
        >
          <span class="p-2 font-weight-600">{{ $t("button.create") }}</span>
        </v-btn>
        <v-btn
          v-if="createdToken"
          :text="false"
          large
          elevation="0"
          class="primary"
          :class="!confirm ? 'disabled-gray' : ''"
          @click="close()"
          :loading="submitting"
        >
          <span class="p-2 font-weight-600">{{ $t("button.continue") }}</span>
          <v-icon size="24" class="ml-2">$arrowright</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "../../apis/Api";
import Checkbox from "@/components/buttons/Checkbox.vue";

export default {
  components: {
    Checkbox,
  },
  data() {
    return {
      opened: false,
      name: "",
      createdToken: null,
      confirm: false,
      submitting: false,
      hoveredIndex: {
        spf: null,
        dkim: null
      },
      valueCopied: false,
    };
  },
  methods: {
    copyElement(event) {
      const el = event.currentTarget.querySelector(".record-box-copy-element");
      if (el) {
        const text = el.innerText || el.textContent;
        navigator.clipboard.writeText(text).then(() => {
          this.valueCopied = true;
        });
      }
    },
    open() {
      this.opened = true;
      this.name = "";
      this.createdToken = null;
      this.confirm = false;
    },
    close() {
      this.opened = false;
    },
    submit() {
      this.submitting = true;
      let params = {
        name: this.name,
      };
      Api.post(`/reseller/api-tokens`, params)
        .then((response) => {
          this.$emit("api-token-created", response.data.data);
          this.createdToken = response.data.data.plain_text_token;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
  watch: {
    // opened(opened) {
    //   if (opened) {
    //     this.$store.dispatch("lockBodyScroll");
    //     return;
    //   }
    //   this.$store.dispatch("unlockBodyScroll");
    // },
  },
};
</script>

<style src="@/components/modal/BasicModal.scss" lang="scss" scoped/>
<style lang="scss" scoped>
  .modal-divider {
    width: 100%;
    height: 1px;
    background-color: var(--v-gray-lighten5);
  }
  .record {
    &-label {
      margin-left: 12px;
      font-size: 12px;
      font-style: normal;
      font-weight: 700;
      padding: 3px 5px;
      border-radius: 5px;
      // text-transform: capitalize;
      height: 24px;
      display: flex;
      align-items: center;

      &.failed {
        color: var(--v-error-base);
        background: rgba(231, 29, 29, 0.15);
      }

      &.configured {
        color: var(--v-primary-darken1);
        background: var(--v-primary-lighten2)
      }
    }

    &-box {
      &-title {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        color: var(--gray-pa-1100-headings, #21212B);
        font-size: 18px;
        line-height: 24px;
      }

      &-list {
        &-item {
          cursor: pointer;

          .copy-icon {
            transition: 0.3s;
            opacity: 0;
            visibility: hidden;
            margin-left: 4px;
            color: var(--v-primary-base);
          }

          &:hover {
            .copy-icon {
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }

      &-copy-element-tooltip {
        width: 50px;
        text-align: center;
      }
    }
  }
</style>